import { useMemo } from "react";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useDebounce } from "app/custom-hooks/useDebounce";
import { useApiQuery } from "app/modules/api-client";
import { INDUSTRY_ENDPOINT_MIN_DATE } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/industryDataAvailability";

import { useIndustryAvailabilityParams } from "../../overview/industry/useIndustryAvailabilityParams";

const FILTER_VALUES_PAGE = 1;
const FILTER_VALUES_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_DELAY = 600;

export type IndustryAdsFilterEntity = "advertisers" | "publishers";

interface IUseIndustryAdsEntityFilterResult {
    values: string[];
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    isSearchPending: boolean;
    isInitialLoading: boolean;
}

const useIndustryAdsFilterValues = (
    category: IFlattenedCategory,
    entity: IndustryAdsFilterEntity,
    searchTerm: string,
    enabled: boolean,
) => {
    // Filter values come out of the recent-ads dataset, so they share its boundary.
    const { params, availability } = useIndustryAvailabilityParams(
        category,
        INDUSTRY_ENDPOINT_MIN_DATE.adintelligenceIndustryRecentAdsList,
    );

    const methodName =
        entity === "advertisers"
            ? "adintelligenceIndustryRecentAdsFilteredAdvertisersList"
            : "adintelligenceIndustryRecentAdsFilteredPublishersList";

    const { data, isLoading, isFetching } = useApiQuery(
        {
            tag: "AdIntelligenceMarketingAssets",
            methodName,
            options: {
                enabled: enabled && !availability.isFullyUnavailable,
                select: (response) => ({
                    values: (response?.data ?? []).map((row) => row.value),
                    totalCount: response?.totalCount ?? 0,
                }),
            },
        },
        {
            ...params,
            page: FILTER_VALUES_PAGE,
            pageSize: FILTER_VALUES_PAGE_SIZE,
            ...(searchTerm ? { search: searchTerm } : {}),
        },
    );

    return {
        values: data?.values ?? [],
        totalCount: data?.totalCount ?? 0,
        isLoading: isLoading && isFetching,
    };
};

export const useIndustryAdsEntityFilter = (
    category: IFlattenedCategory,
    entity: IndustryAdsFilterEntity,
): IUseIndustryAdsEntityFilterResult => {
    const [debouncedSearchTerm, setSearchTerm, searchTerm] = useDebounce("", SEARCH_DEBOUNCE_DELAY);

    const {
        values: loadedValues,
        totalCount,
        isLoading: isInitialLoading,
    } = useIndustryAdsFilterValues(category, entity, "", true);

    const localMatches = useMemo(() => {
        if (!searchTerm) {
            return loadedValues;
        }
        const term = searchTerm.toLowerCase();
        return loadedValues.filter((value) => value.toLowerCase().includes(term));
    }, [loadedValues, searchTerm]);

    const hasLocalMatch = localMatches.length > 0;
    // A single page holds every value unless totalCount says otherwise, in which
    // case the rest is only reachable through the endpoint's own search.
    const hasUnloadedValues = totalCount > loadedValues.length;
    const isRemoteSearchEnabled = Boolean(debouncedSearchTerm) && !hasLocalMatch && hasUnloadedValues;

    const { values: remoteValues, isLoading: isRemoteLoading } = useIndustryAdsFilterValues(
        category,
        entity,
        debouncedSearchTerm,
        isRemoteSearchEnabled,
    );

    const isSearchPending =
        Boolean(searchTerm) &&
        !hasLocalMatch &&
        hasUnloadedValues &&
        (searchTerm !== debouncedSearchTerm || isRemoteLoading);

    return {
        values: hasLocalMatch ? localMatches : remoteValues,
        searchTerm,
        setSearchTerm,
        isSearchPending,
        isInitialLoading,
    };
};
